import React, { useEffect, createContext, useState, useContext, useRef } from 'react';
import { SuiNotificationGroup, SuiNotification } from '@splashthat/sui';
import PropTypes from 'prop-types';

const NotificationManagerContext = createContext();
const DEFAULT_NOTIFICATION_DURATION = 5000;

const normalizeNotification = ({ id, duration, message, ...restOfProperties }) => ({
    ...restOfProperties,
    message,
    id: id || message,
    duration: duration === undefined ? DEFAULT_NOTIFICATION_DURATION : duration,
});

const NotificationManager = ({ children }) => {
    const [notificationList, setNotificationList] = useState([]);
    const notificationListRef = useRef(notificationList);

    useEffect(() => {
        // Saving notifications in refs because they need to be accessed by a setTimeout callback
        notificationListRef.current = notificationList;
    }, [notificationList]);

    useEffect(
        // Cleanup-only effect, clears timeouts of notifications that remain open
        () => () => {
            notificationListRef.current.forEach((notification) => {
                clearTimeout(notification.timer);
            });
        },
        [],
    );

    const hideNotificationById = (notificationId) => {
        const notificationToHide = notificationListRef.current.find(({ id }) => id === notificationId);
        clearTimeout(notificationToHide?.timer);
        setNotificationList(notificationListRef.current.filter(({ id }) => id !== notificationId));
    };

    const hideNotification = (notification) => {
        hideNotificationById(normalizeNotification(notification).id);
    };

    const showNotification = (notification) => {
        const newNotification = normalizeNotification(notification);
        const notificationsExists = notificationList.includes(({ id }) => id === newNotification.id);
        if (!notificationsExists) {
            if (typeof newNotification.duration === 'number' && newNotification.duration > 0) {
                newNotification.timer = setTimeout(() => {
                    hideNotificationById(newNotification.id);
                }, newNotification.duration);
            }
            setNotificationList([...notificationList, newNotification]);
        }
    };

    return (
        <NotificationManagerContext.Provider value={{ showNotification, hideNotification }}>
            {children}
            <SuiNotificationGroup notifications={notificationList} closeNotification={hideNotificationById} />
        </NotificationManagerContext.Provider>
    );
};

NotificationManager.propTypes = {
    children: PropTypes.node.isRequired,
};

export const NOTIFICATION_STATUSES = {
    ERROR: SuiNotification.ERROR,
    SUCCESS: SuiNotification.SUCCESS,
};

export const useNotificationManager = () => useContext(NotificationManagerContext);
export default NotificationManager;
